"use client";

import type { ReactNode } from "react";
import { HandHeartIcon } from "@phosphor-icons/react";
import {
  Button,
  NotificationBanner,
} from "@boostdev/design-system-components/client";
import css from "./BuyMeACoffeePopover.module.css";

export interface BuyMeACoffeeLabels {
  readonly title: string;
  readonly body: string;
  readonly cta: string;
  /** @deprecated kept on the type for backwards-compat; the QR image
   *  is no longer rendered. */
  readonly qrAlt?: string;
}

/** Body copy for the "free code" variant: a single sentence whose
 *  `<email>…</email>` segment becomes an inline mailto link to `email`. */
export interface BuyMeACoffeeFreeBody {
  readonly text: string;
  readonly email: string;
}

export interface BuyMeACoffeePopoverProps {
  readonly href: string;
  readonly labels: BuyMeACoffeeLabels;
  /** When set, replaces the donation body with the email-help message. */
  readonly freeBody?: BuyMeACoffeeFreeBody;
}

const EMAIL_OPEN = "<email>";
const EMAIL_CLOSE = "</email>";

/**
 * Turns a single `<email>linked text</email>` marker into a real mailto
 * anchor, mirroring the inline-link approach used by the photo-code intro.
 * Without a well-formed marker the text renders verbatim.
 */
function renderBodyWithEmailLink({ text, email }: BuyMeACoffeeFreeBody): ReactNode {
  const open = text.indexOf(EMAIL_OPEN);
  const close = text.indexOf(EMAIL_CLOSE);
  if (open === -1 || close === -1 || close < open) return text;
  return (
    <>
      {text.slice(0, open)}
      <a href={`mailto:${email}`} aria-label={`Email ${email}`}>
        {text.slice(open + EMAIL_OPEN.length, close)}
      </a>
      {text.slice(close + EMAIL_CLOSE.length)}
    </>
  );
}

export function BuyMeACoffeePopover({
  href,
  labels,
  freeBody,
}: BuyMeACoffeePopoverProps) {
  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={labels.title}
      className={css.wrapper}
    >
      <NotificationBanner
        variant="info"
        action={
          <Button
            isIconOnly
            className={css.cta}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={labels.cta}
          >
            <HandHeartIcon
              size={24}
              weight="regular"
              aria-hidden
              className={css.iconDefault}
            />
            <HandHeartIcon
              size={24}
              weight="duotone"
              aria-hidden
              className={css.iconHover}
            />
          </Button>
        }
      >
        <div className={css.text}>
          <strong className={css.title}>{labels.title}</strong>
          <p className={css.body}>
            {freeBody ? renderBodyWithEmailLink(freeBody) : labels.body}
          </p>
        </div>
      </NotificationBanner>
    </div>
  );
}
